import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Block, BlockType } from '../../../../../config/api/models/Block';
import { ConfirmationDialog, Icons } from '../../../../shared/components/';
import { TrajectorySectionOrWorksheetType } from '../../../../../config/api/models/Trajectory';
import { HoverBlockIconButton, HoverBlockTitle, HoverBlockWrapper } from './atoms/Atoms';
import { useParams } from 'react-router';
import { UrlParams } from '../../../../shared/types';
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/useAppDispatch';
import {
  deleteBlock,
  deleteSection,
  patchBlock,
  releaseCluster,
} from '../../../../shared/slices/blocks/api/blocks';
import {
  setBlockToDelete,
  setFocusBlock,
} from '../../../../shared/slices/blocks/entity/BlocksSlice';

import CustomMenuList from '../../../../shared/components/CustomMenuList';
import { CustomMenuItem } from '../../../../../config/api/models/CustomMenu';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { Button, Theme, styled } from '@material-ui/core';
import { rgba } from 'polished';
import { TFunction } from 'i18next';

type Props = {
  pageId: string;
  block: Block;
  type: TrajectorySectionOrWorksheetType;
  clusterId?: string;
  sectionId?: string;
};

const getTitle = (block: Block, t: TFunction) => {
  let title = '';
  const { type } = block;
  switch (type) {
    case 'section':
      title = 'Sectie';
      break;
    case 'cluster':
      title = 'Cluster';
      break;
    case 'text_exercise':
      title = block.title ?? 'Oefening';
      break;

    default:
      title = t(`trajectory.page.blocks.${block.type}.title`);
      break;
  }
  return title;
};

export const TitleButton = styled(Button)<Theme>(({ theme }) => ({
  textTransform: 'none',
  margin: 0,
  border: '1px solid #0092BC',
  borderRadius: 0,
  padding: 2,
  fontWeight: 600,
  color: '#0092BC', // TODO - add this to mui theme
  background: 'white',
  '&:hover': {
    backgroundColor: rgba(0, 146, 188, 0.1),
  },
}));

const HoverBlock: FC<Props> = ({ pageId, block, type, clusterId, sectionId }) => {
  const { t } = useTranslation();
  const { bookId } = useParams<UrlParams>();
  const isOpen = useAppSelector((state) => state.sidepanel.isOpen);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const blocks = useAppSelector((state) => state.blocks.blocks);
  const dispatch = useAppDispatch();

  const showTrashicon =
    clusterId &&
    block.type === 'section' &&
    blocks
      ?.filter((item) => item.id === clusterId)[0]
      .sections?.findIndex((itemIndex) => itemIndex.id === block.id) === 0;

  const handleOnClose = useCallback(
    (result: boolean) => {
      if (result) {
        if (block.type !== 'cluster' && block.type !== 'section') {
          dispatch(
            deleteBlock({
              pageId,
              bookId: bookId as string,
              blockId: block.id,
              cluster_id: clusterId,
              section_id: sectionId,
            })
          );
        } else if (clusterId && block.type === 'section') {
          dispatch(
            deleteSection({
              clusterId,
              sectionId: block.id,
            })
          );
        } else if (block.id && block.type === 'cluster') {
          dispatch(
            releaseCluster({
              pageId,
              clusterId: block.id,
            })
          );
        }
      }
      setOpenConfirmDelete(false);
      dispatch(setBlockToDelete(undefined));
    },
    [pageId, block, bookId, dispatch, clusterId, sectionId]
  );

  const handleOpenConfirmation = useCallback(() => {
    dispatch(setBlockToDelete(block.id));
    setOpenConfirmDelete(true);
  }, [block.id, dispatch]);

  const defaultPayload = React.useMemo(() => {
    return {
      pageId,
      bookId: bookId as string,
      blockId: block.id,
      payload: {
        type: BlockType.COLUMN,
      },
    };
  }, [block.id, bookId, pageId]);

  const menuItemsEntries: CustomMenuItem[] = [
    {
      label: '50% - 50%',
      onClick: () => {
        const settings = [50, 50];
        dispatch(
          patchBlock({
            ...defaultPayload,
            payload: {
              ...defaultPayload.payload,
              columns: block.columns?.map((col, index) => ({
                content: col.content,
                id: col.id,
                width: settings[index],
              })),
            },
          })
        );
      },
    },
    {
      label: '65% - 35%',
      onClick: () => {
        const settings = [65, 35];
        dispatch(
          patchBlock({
            ...defaultPayload,
            payload: {
              ...defaultPayload.payload,
              columns: block.columns?.map((col, index) => ({
                id: col.id,
                content: col.content,
                width: settings[index],
              })),
            },
          })
        );
      },
    },
    {
      label: '35% - 65%',
      onClick: () => {
        const settings = [35, 65];
        dispatch(
          patchBlock({
            ...defaultPayload,
            payload: {
              ...defaultPayload.payload,
              columns: block.columns?.map((col, index) => ({
                id: col.id,
                content: col.content,
                width: settings[index],
              })),
            },
          })
        );
      },
    },
  ];

  return (
    <HoverBlockWrapper studyGuide={type === 'study_guide'}>
      {type !== 'study_guide' && (
        <HoverBlockTitle style={{ pointerEvents: isOpen ? 'inherit' : 'none' }}>
          <TitleButton
            onMouseOver={() => {
              dispatch(setFocusBlock(block.id));
            }}
          >
            {getTitle(block, t)}
          </TitleButton>
        </HoverBlockTitle>
      )}
      {block.type === BlockType.COLUMN && (
        <>
          <CustomMenuList
            icon={SpriteIconsLight.PEN}
            menuItemsList={menuItemsEntries}
            square
            style={{
              right: 0,
              position: 'absolute',
              transform: 'translate(-100%, -50%)',
              zIndex: 9999,
            }}
          ></CustomMenuList>
        </>
      )}

      {!showTrashicon && (
        <HoverBlockIconButton
          transform={type === 'study_guide' ? 'none' : 'translateY(-50%)'}
          tooltip={t('shared.delete')}
          icon={Icons.DELETE}
          onClick={handleOpenConfirmation}
        />
      )}

      <ConfirmationDialog
        open={openConfirmDelete}
        title={t('trajectory.sidebar.pageManagement.confirmDelete.title')}
        description={t('trajectory.sidebar.pageManagement.confirmDelete.description')}
        onClose={handleOnClose}
      />
    </HoverBlockWrapper>
  );
};

export default HoverBlock;
